/**
 * A Workshop document laid out as a document (stage 3): the facts strip,
 * definition rows, numbered sections under an index that follows the reading,
 * tables kept as tables, and everything else through the markdown renderer.
 *
 * The same layout is the editor. In edit mode every block is a target; the one
 * being edited turns into a box of its own markdown where it sits, in the
 * block's own type, so the stored text only changes where the person typed.
 * With the agent's changes under review, each changed passage shows what it
 * was, dimmed, above what it is now.
 */
import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { cn } from "@/utils/helpers";
import type { DocBlock, DocChange, DocFact, ParsedDocument } from "./documentBlocks";

const EMPHASIS =
  /(\*\*|__)(?=\S)(.+?)(?<=\S)\1|(?<![\w*])(\*|_)(?=\S)([^*_\n]+?)(?<=\S)\3(?![\w*])|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]*)\)/g;
const SAFE_HREF = /^(?:https?:|mailto:|\/|#)/i;

/** The inline marks a label, a fact or a row carries: bold, italic, code, links. */
function Inline({ text }: { text: string }) {
  const clean = scrubVendorTerms(text);
  const parts: ReactNode[] = [];
  let at = 0;
  for (const match of clean.matchAll(EMPHASIS)) {
    const index = match.index ?? 0;
    if (index > at) parts.push(clean.slice(at, index));
    const key = `${index}`;
    if (match[2] !== undefined) parts.push(<strong key={key}>{match[2]}</strong>);
    else if (match[4] !== undefined) parts.push(<em key={key}>{match[4]}</em>);
    else if (match[5] !== undefined) parts.push(<code key={key}>{match[5]}</code>);
    else if (match[6] !== undefined) {
      const href = match[7] ?? "";
      parts.push(
        SAFE_HREF.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {match[6]}
          </a>
        ) : (
          match[6]
        ),
      );
    }
    at = index + match[0].length;
  }
  if (at < clean.length) parts.push(clean.slice(at));
  return <>{parts}</>;
}

const KIND_LABEL: Record<DocBlock["kind"], string> = {
  facts: "the facts",
  frontmatter: "the front matter",
  title: "the heading",
  section: "the section heading",
  subhead: "the heading",
  heading: "the heading",
  paragraph: "the paragraph",
  definition: "the row",
  list: "the list",
  table: "the table",
  quote: "the quote",
  code: "the code",
  rule: "the rule",
};

function excerpt(block: DocBlock): string {
  const words = (block.text ?? block.definition?.label ?? block.source)
    .replace(/[#>*_`|[\]()-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return words.length > 72 ? `${words.slice(0, 71)}…` : words;
}

function Facts({ facts }: { facts: DocFact[] }) {
  return (
    <dl className="ws-doc-facts">
      {facts.map((fact, index) => (
        <div key={`${fact.label}-${index}`} className="ws-doc-fact">
          <dt>{scrubVendorTerms(fact.label)}</dt>
          <dd>
            <Inline text={fact.value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One block as it reads. `was` is a row's value before the agent changed it;
 * `plain` draws every block through the markdown renderer (a document that
 * does not fit the structured layout).
 */
const BlockView = memo(function BlockView({
  block,
  was,
  plain = false,
}: {
  block: DocBlock;
  was?: string | null;
  plain?: boolean;
}) {
  if (plain) return <ChatMarkdown className="streamdown-doc ws-doc-md" content={block.source} />;
  switch (block.kind) {
    case "facts":
      return <Facts facts={block.facts ?? []} />;
    case "frontmatter":
      return <pre className="ws-doc-front">{block.source}</pre>;
    case "title":
      return <h3 className="ws-doc-h1">{scrubVendorTerms(block.text ?? "")}</h3>;
    case "section":
      return (
        <h3
          id={`ws-doc-${block.key}`}
          className="ws-doc-section"
          data-num={block.number ?? undefined}
          data-section-key={block.key}
        >
          {scrubVendorTerms(block.text ?? "")}
        </h3>
      );
    case "subhead":
      return <h4 className="ws-doc-subhead">{scrubVendorTerms(block.text ?? "")}</h4>;
    case "heading":
      return <h5 className="ws-doc-minor">{scrubVendorTerms(block.text ?? "")}</h5>;
    case "definition":
      // The row itself is the block's own element (see ArtifactDocument).
      return (
        <>
          <dt>
            <Inline text={block.definition?.label ?? ""} />
          </dt>
          <dd>
            {was !== undefined && was !== null ? (
              <span className="ws-doc-was-value" data-note-skip>
                <Inline text={was} />
              </span>
            ) : null}
            <span className={was !== undefined && was !== null ? "ws-doc-now-value" : undefined}>
              <Inline text={block.definition?.value ?? ""} />
            </span>
          </dd>
        </>
      );
    case "table":
      return <ChatMarkdown className="streamdown-doc ws-doc-md ws-doc-table" content={block.source} />;
    default:
      return <ChatMarkdown className="streamdown-doc ws-doc-md" content={block.source} />;
  }
}, sameBlockView);

/** A block that reads the same is not drawn again when the text before it moves. */
function sameBlockView(
  a: { block: DocBlock; was?: string | null; plain?: boolean },
  b: { block: DocBlock; was?: string | null; plain?: boolean },
): boolean {
  return (
    a.was === b.was &&
    a.plain === b.plain &&
    a.block.kind === b.block.kind &&
    a.block.source === b.block.source &&
    a.block.number === b.block.number &&
    // A section's heading carries its key for the index.
    (a.block.kind !== "section" || a.block.key === b.block.key)
  );
}

/**
 * The index: one entry per section. A click scrolls to it; the section being
 * read is lit as the document scrolls.
 */
function DocIndex({
  sections,
  scrollRef,
}: {
  sections: DocBlock[];
  scrollRef: RefObject<HTMLElement | null>;
}) {
  const [current, setCurrent] = useState<string | null>(sections[0]?.key ?? null);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      const top = scroller.getBoundingClientRect().top;
      let lit = sections[0]?.key ?? null;
      for (const section of sections) {
        const heading = scroller.querySelector<HTMLElement>(`[data-section-key="${section.key}"]`);
        if (!heading) continue;
        if (heading.getBoundingClientRect().top - top > 96) break;
        lit = section.key;
      }
      // Scrolled to the end: the last section is the one being read.
      const atEnd = scroller.scrollTop > 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      if (atEnd) lit = sections[sections.length - 1]?.key ?? lit;
      setCurrent(lit);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(read);
    };
    read();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [scrollRef, sections]);

  const go = (key: string) => {
    const heading = scrollRef.current?.querySelector<HTMLElement>(`[data-section-key="${key}"]`);
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    heading?.scrollIntoView({ block: "start", behavior: still ? "auto" : "smooth" });
    setCurrent(key);
  };

  return (
    <nav className="ws-doc-index" aria-label="Sections" data-note-skip>
      {sections.map((section) => {
        const label = scrubVendorTerms(section.text ?? "");
        return (
          <button
            key={section.key}
            type="button"
            className="ws-doc-jump"
            aria-current={current === section.key ? "true" : undefined}
            title={label}
            onClick={() => go(section.key)}
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

/** What the reader needs from the editor while a document is being edited. */
export type DocEditing = {
  /** The block open for editing, by key in the draft being edited. */
  activeKey: string | null;
  /** Its markdown as typed so far. */
  text: string;
  onActivate: (block: DocBlock) => void;
  onText: (text: string) => void;
  /** Leave the block; `refocus` puts focus back on it (Escape). */
  onLeave: (refocus: boolean) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** The format bar, drawn over the block being edited (null where it docks elsewhere). */
  bar: ReactNode;
};

/** The block being edited: its markdown in a box the size of its text, in its own type. */
function BlockEditor({ block, editing }: { block: DocBlock; editing: DocEditing }) {
  const { text, textareaRef, onText, onLeave, bar } = editing;
  useLayoutEffect(() => {
    const box = textareaRef.current;
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${box.scrollHeight}px`;
  }, [text, textareaRef]);

  return (
    <>
      {bar}
      <textarea
        ref={textareaRef}
        className="ws-doc-editor"
        data-kind={block.kind}
        value={text}
        rows={1}
        aria-label={`Editing ${KIND_LABEL[block.kind]}, as markdown`}
        // A CRLF document's block comes back LF, on purpose: textareas hand back LF anyway.
        onChange={(event) => onText(event.target.value.replace(/\r\n?/g, "\n"))}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          onLeave(true);
        }}
        onBlur={(event) => {
          const next = event.relatedTarget;
          // Moving into the format bar keeps the block open.
          if (next instanceof Element && next.closest("[data-doc-format-bar]")) return;
          onLeave(false);
        }}
      />
    </>
  );
}

type ChangePlan = {
  /** A block this version added: its change. */
  added: Map<string, DocChange>;
  /** Earlier text shown before a block (or at the end, under ""). */
  before: Map<string, DocChange[]>;
  /** A row whose value changed: the value it had. */
  rowWas: Map<string, string>;
  /** Which element each change starts at: its earlier text, or its first new block. */
  startsAt: Map<string, string>;
};

function planChanges(changes: DocChange[]): ChangePlan {
  const plan: ChangePlan = { added: new Map(), before: new Map(), rowWas: new Map(), startsAt: new Map() };
  for (const change of changes) {
    for (const block of change.added) plan.added.set(block.key, change);
    const [was] = change.removed;
    const [now] = change.added;
    const sameRow =
      change.removed.length === 1 &&
      change.added.length === 1 &&
      was.definition &&
      now.definition &&
      was.definition.label.toLowerCase() === now.definition.label.toLowerCase();
    if (sameRow && was.definition) {
      plan.rowWas.set(now.key, was.definition.value);
      plan.startsAt.set(change.id, now.key);
      continue;
    }
    if (change.removed.length > 0) {
      const at = change.anchorKey ?? "";
      plan.before.set(at, [...(plan.before.get(at) ?? []), change]);
      plan.startsAt.set(change.id, `was-${change.id}`);
    } else if (now) {
      plan.startsAt.set(change.id, now.key);
    }
  }
  return plan;
}

/** A block drawn on its own (earlier text): a row keeps its list around it. */
function StaticBlock({ block, plain }: { block: DocBlock; plain: boolean }) {
  if (plain || block.kind !== "definition") return <BlockView block={block} plain={plain} />;
  return (
    <dl className="ws-doc-defs">
      <div className="ws-doc-def">
        <BlockView block={block} />
      </div>
    </dl>
  );
}

type Props = {
  doc: ParsedDocument;
  /** The H1 that only repeats the output's title: not drawn. */
  hiddenKey: string | null;
  /** The section index under the facts (not on a phone). */
  showIndex: boolean;
  scrollRef: RefObject<HTMLElement | null>;
  /** The agent's changes under review, and the passage being looked at. */
  changes?: DocChange[] | null;
  currentChangeId?: string | null;
  editing?: DocEditing | null;
};

export default function ArtifactDocument({
  doc,
  hiddenKey,
  showIndex,
  scrollRef,
  changes = null,
  currentChangeId = null,
  editing = null,
}: Props) {
  const plan = useMemo(() => planChanges(changes ?? []), [changes]);
  const plain = !doc.structured;
  const indexAt = showIndex && !plain && doc.sections.length >= 2 ? doc.sections[0].key : null;

  const items: ReactNode[] = [];
  let rows: ReactNode[] = [];
  const flushRows = () => {
    if (rows.length === 0) return;
    items.push(
      <dl key={`rows-${items.length}`} className="ws-doc-defs">
        {rows}
      </dl>,
    );
    rows = [];
  };
  const pushEarlier = (list: DocChange[]) => {
    flushRows();
    for (const change of list) {
      items.push(
        <div
          key={`was-${change.id}`}
          id={`ws-change-${change.id}`}
          className="ws-doc-was"
          data-note-skip
          data-current={change.id === currentChangeId || undefined}
        >
          <span className="sr-only">Before this change:</span>
          {change.removed.map((block) => (
            <StaticBlock key={block.key} block={block} plain={plain} />
          ))}
        </div>,
      );
    }
  };

  doc.blocks.forEach((block, position) => {
    if (block.key === hiddenKey) return;
    if (block.key === indexAt) {
      flushRows();
      items.push(<DocIndex key="index" sections={doc.sections} scrollRef={scrollRef} />);
    }
    const earlier = plan.before.get(block.key);
    if (earlier) pushEarlier(earlier);

    const change = plan.added.get(block.key);
    const active = !!editing && editing.activeKey === block.key;
    const isRow = !plain && block.kind === "definition";
    const element = (
      <div
        // By place, not by offset: a block keeps its element when one before it grows.
        key={`${position}-${block.kind}`}
        id={change && plan.startsAt.get(change.id) === block.key ? `ws-change-${change.id}` : undefined}
        className={cn("ws-doc-block", isRow && "ws-doc-def", active && "is-active")}
        data-kind={block.kind}
        data-start={block.start}
        data-changed={change ? true : undefined}
        data-current={change && change.id === currentChangeId ? true : undefined}
      >
        {editing && !active ? (
          <button
            type="button"
            className="ws-doc-hit"
            aria-label={`Edit ${KIND_LABEL[block.kind]}: ${excerpt(block)}`}
            // Opened on press, before the open block loses focus, so both
            // happen as one step; a key press (Enter, Space) comes as a click
            // (after a press the button is gone, so a click never repeats it).
            onMouseDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              editing.onActivate(block);
            }}
            onClick={() => editing.onActivate(block)}
          />
        ) : null}
        {active && editing ? (
          <BlockEditor block={block} editing={editing} />
        ) : (
          <BlockView block={block} was={plan.rowWas.get(block.key)} plain={plain} />
        )}
      </div>
    );
    if (isRow) rows.push(element);
    else {
      flushRows();
      items.push(element);
    }
  });
  const atEnd = plan.before.get("");
  if (atEnd) pushEarlier(atEnd);
  flushRows();

  return (
    <div
      className="ws-doc"
      data-plain={plain || undefined}
      data-editing={editing ? true : undefined}
      data-reviewing={changes && changes.length > 0 ? true : undefined}
    >
      {items}
    </div>
  );
}
